/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { createApi } from "@reduxjs/toolkit/query/react"

import { createRequestHeaders } from "queries/apiClient"
import { serializeQueryArgsExcludingUiOptions } from "queries/serializeQueryArgs"
import { HttpMethod } from "queries/types"
import {
  returnRefundApiReducerPath,
  returnRefundEndpoints,
  RETURN_REFUND_CACHE_TAGS,
} from "./constants"
import { returnRefundBaseQuery } from "./returnRefundBaseQuery"

// Request/Response types for endpoint params and responses.
// Hand-authored from the MEX-side Return/Refund backend spec (delv-platform-api) — replace
// with generated types once the BE contract ships its own generated types.

export interface ReturnRefundWorklistPagination {
  /** One-based page number. Omit or send zero for the first page. */
  page?: number
  /** Maximum number of requests, not items. The server caps this at 50. */
  limit?: number
}

export interface ListReturnRefundWorklistParams {
  merchantID: string
  /** Inclusive lower bound on the claimed item's `submittedAt`, epoch milliseconds. */
  submittedAtFrom?: number
  /** Exclusive upper bound on the claimed item's `submittedAt`, epoch milliseconds. */
  submittedAtTo?: number
  statusFilter?: string[]
  pagination?: ReturnRefundWorklistPagination
}

export interface ReturnRefundStatusBadge {
  text?: string
  textColor?: string
  backgroundColor?: string
}

export interface ReturnRefundWorklistItem {
  itemID?: string
  itemName?: string
  quantity?: number
  /** Display-only value. Eligibility, routing, and decisions are server-owned. */
  skuValueDisplay?: string
  reason?: string
  reasonDisplayName?: string
  reviewOwner?: string
  /**
   * This item's own SLA deadline.
   *
   * **Unit unconfirmed with BE** — no sample response has carried this field yet. Treat it as
   * epoch milliseconds, matching `submittedAt` on the same message, and confirm before
   * rendering a countdown from it. Do NOT pass it to `dayjs.unix()`, which expects seconds:
   * on a millisecond value that renders a year in the tens of thousands.
   */
  slaDueAt?: number
  /** Epoch **milliseconds** — same unit as the `submittedAtFrom`/`submittedAtTo` filters. */
  submittedAt?: number
  /**
   * This item's own status, despite the name. The server resolves it with the same function it
   * uses for the request, passing only this item — so it can differ from the row's
   * `requestStatus`, which is resolved across every item in the request.
   */
  requestStatus?: string
  /** Display text for this item's own status. */
  requestStatusContent?: string
}

export interface ReturnRefundWorklistRequestRow {
  orderID?: string
  bookingCode?: string
  shortOrderNumber?: string
  requestID?: string
  requestStatus?: string
  /** Server-rendered display text for `requestStatus`, e.g. `Pending acknowledgement`. */
  requestStatusContent?: string
  /** e.g. 'ONGOING', 'ACTION_REQUIRED' */
  bucket?: string
  nextAction?: string
  actionRequiredBy?: string
  /**
   * Deadline for the merchant's next action, and what the Orders table's SLA countdown is
   * derived from.
   *
   * **Unit unconfirmed with BE** — no sample response has carried this field yet. Treat it as
   * epoch milliseconds, matching `submittedAt` on the same message, and confirm before
   * rendering a countdown from it. Do NOT pass it to `dayjs.unix()`, which expects seconds:
   * a seconds-based reading of a millisecond value is 1000× wrong.
   */
  nextActionDueAt?: number
  items?: ReturnRefundWorklistItem[]
  badge?: ReturnRefundStatusBadge
}

export interface ReturnRefundBucketCount {
  bucket: string
  requestCount: number
}

export interface ListReturnRefundWorklistMetadata {
  /**
   * Global bucket counts for the merchant scope. These do **not** respect `statusFilter` and are
   * independent of the current page, so they cannot be used as filtered result counts.
   */
  bucketCounts?: ReturnRefundBucketCount[]
  /** Total requests across the merchant scope and applied filters. */
  totalCount?: number
}

export interface ListReturnRefundWorklistResponse {
  /** A request and all of its claimed items are returned together; never split across pages. */
  requests?: ReturnRefundWorklistRequestRow[]
  metadata?: ListReturnRefundWorklistMetadata
}

export interface GetReturnRefundReviewDetailParams {
  /**
   * Scope header only — the endpoint itself takes no merchantID; the server resolves the
   * merchant from the authenticated context and 404s an order it does not own.
   */
  merchantID: string
  /**
   * The detail endpoint is keyed by ORDER, not request — MVP allows one submitted
   * request per delivered order, so the order identifies it.
   */
  orderID: string
}

/** One buyer-evidence (or merchant draft) media entry. */
export interface ReturnRefundMediaAsset {
  /** Galaxy imageID or videoID. */
  assetID?: string
  url?: string
  fileName?: string
  fileType?: string
  /** e.g. `RETURN_REFUND_MEDIA_TYPE_PHOTO`. */
  mediaType?: string
  linkStatus?: string
}

export interface ReturnRefundMedia {
  photos?: ReturnRefundMediaAsset[]
  videos?: ReturnRefundMediaAsset[]
}

/** The buyer's claimed reason. Display copy (`reasonDisplayName`) travels separately. */
export interface ReturnRefundReviewReason {
  reasonCode?: string
  reasonGroup?: string
  /** Drives eligibility server-side (return-and-refund vs refund-only reasons). */
  resolutionType?: string
}

/**
 * Per-item status badge. `statusCode` carries the item's `ItemStatus` enum value; the
 * copy/colour fields are the presentation contract and may arrive unpopulated.
 */
export interface ReturnRefundReviewItemStatus extends ReturnRefundStatusBadge {
  statusCode?: string
}

export interface ReturnRefundReviewFulfillment {
  fulfillmentRefID?: string
  method?: string
  status?: string
  trackingURL?: string
  scheduleDisplay?: string
}

export interface ReturnRefundReviewItem {
  itemID?: string
  itemName?: string
  quantity?: number
  /** Display-only value. Eligibility, routing, and decisions are server-owned. */
  skuValueDisplay?: string
  thumbnailURL?: string
  reason?: ReturnRefundReviewReason
  reasonDisplayName?: string
  /** Buyer's own words. */
  issueDescription?: string
  /** Buyer's uploaded evidence. */
  media?: ReturnRefundMedia
  reviewOwner?: string
  status?: ReturnRefundReviewItemStatus
  /**
   * Decisions this merchant may submit for this item, e.g.
   * `MEX_RETURN_REFUND_RESOLUTION_APPROVE_RETURN_AND_REFUND`. Server-owned — absent or
   * empty means the item is not currently decidable.
   */
  eligibleResolutions?: string[]
  /** True once the single formal info request has been spent on this item. */
  infoRequestUsed?: boolean
  returnFulfillment?: ReturnRefundReviewFulfillment
  displayModifierGroup?: string
  /** Merchant's own staged evidence (uploads pending a later submission). */
  draftMedia?: ReturnRefundMedia
}

/**
 * One server-rendered timeline row. The contract is explicit that the client renders
 * these as supplied and must not derive steps from item statuses.
 */
export interface ReturnRefundReviewTimelineStep {
  stepCode?: string
  title?: string
  /** `DONE` / `CURRENT` / `UPCOMING`. */
  stepState?: string
  subtitle?: string
  dotColor?: string
  /** Epoch milliseconds as a decimal string when the step has a timestamp; empty otherwise. */
  detail?: string
  actionName?: string
  /** e.g. `DEEPLINK`. */
  actionType?: string
  /** The action's destination (tracking URL / deeplink), when it has one. */
  value?: string
}

export interface ReturnRefundReviewTimelineAction {
  deadlineLabel?: string
  /** Unix epoch SECONDS — unlike every worklist timestamp. Drives the live countdown. */
  dueAt?: number
  actionLabel?: string
}

export interface ReturnRefundReviewTimeline {
  title?: string
  steps?: ReturnRefundReviewTimelineStep[]
  action?: ReturnRefundReviewTimelineAction
}

/** One item's decision in the submit payload. */
export interface ReturnRefundItemDecision {
  itemID: string
  /** A `MEX_RETURN_REFUND_RESOLUTION_*` value from the item's `eligibleResolutions`. */
  decision: string
  /** Required by the flow when the decision is a rejection. */
  rejectionReason?: string
  rejectionNote?: string
  /**
   * Echoed even when empty — item identity server-side is the PAIR
   * (itemID, displayModifierGroup).
   */
  displayModifierGroup: string
}

export interface SubmitReturnRefundReviewDecisionParams {
  /** Scope header only, like the detail query — the endpoint body carries no merchant. */
  merchantID: string
  orderID: string
  requestID: string
  decisions: ReturnRefundItemDecision[]
}

export interface SubmitReturnRefundReviewDecisionResponse {
  requestID?: string
  orderID?: string
  requestStatus?: string
  /** Message Center room for the buyer conversation, when the decision opened one. */
  roomID?: string
}

/**
 * The file portion of a media upload, base64-encoded.
 *
 * The wire shapes here follow the deployed Go DTO (`ReturnRefundMediaUpload` /
 * `FileInfo` in delv-platform-api), NOT the contract doc's example payload: the
 * DTO decodes with plain `encoding/json`, so `size`/`fileSize` are int64 NUMBERS
 * (the doc's string form 400s with "failed to decode request") and `mediaType`
 * is a protobuf enum NUMBER (`1` = PHOTO — proto-v2 enums have no string
 * unmarshalling). `mediaSource` alone is a Go string type, so it stays a string.
 */
export interface ReturnRefundMediaUploadFile {
  name: string
  /** Bare extension (`jpg`/`jpeg`/`png`), never a MIME type. */
  type: string
  /** Byte count as a NUMBER — the DTO field is int64. */
  size: number
  /** Raw base64, no `data:` URL prefix. */
  data: string
}

export interface ReturnRefundMediaUpload {
  /** Proto enum NUMBER: `1` = PHOTO — the endpoint accepts photos only. */
  mediaType: number
  file: ReturnRefundMediaUploadFile
  fileName: string
  fileType: string
  /** Byte count as a number, mirroring `file.size`. */
  fileSize: number
  /** e.g. `RETURN_REFUND_MEDIA_SOURCE_DEVICE_LIBRARY` for a web file picker. */
  mediaSource: string
}

export interface UploadReturnRefundMediaParams {
  /** Scope header only, like the other endpoints — the body carries no merchant. */
  merchantID: string
  orderID: string
  itemID: string
  /** Echoed even when empty — item identity is the (itemID, displayModifierGroup) pair. */
  displayModifierGroup: string
  /**
   * `REVIEW` while the item awaits decision 1, `INSPECTION` while it awaits
   * inspection — the server 409s an upload whose purpose does not match the item's
   * current stage.
   */
  uploadPurpose: string
  /**
   * `ADD` / `REPLACE` / `REMOVE` — see `RETURN_MEDIA_OPERATION` for the field
   * pairings each one requires. Omitted, the server infers add-or-replace from
   * `mediaAssetIDToReplace`.
   */
  operation?: string
  /** The asset a `REPLACE` swaps, or the one a `REMOVE` deletes. */
  mediaAssetIDToReplace?: string
  /** Absent on `REMOVE`, which deletes by asset id alone. */
  media?: ReturnRefundMediaUpload
  /** Set when the caller shows its own failure message for this call. */
  suppressErrorToast?: boolean
}

export interface UploadReturnRefundMediaResponse {
  media?: ReturnRefundMediaAsset
}

/** One item's inspection outcome in the submit payload. */
export interface ReturnedItemInspection {
  itemID: string
  /** `APPROVE` or `REPORT_ISSUE`. */
  outcome: string
  /**
   * Required when the outcome reports an issue; free text up to 250 characters
   * server-side (the FE sends its reason codes).
   */
  issueReason?: string
  issueNote?: string
  /**
   * Echoed even when empty — item identity server-side is the PAIR
   * (itemID, displayModifierGroup).
   */
  displayModifierGroup: string
}

export interface SubmitReturnedItemInspectionParams {
  /** Scope header only, like the other endpoints — the body carries no merchant. */
  merchantID: string
  orderID: string
  /** Must be the submitted request's UUID — the server validates the format. */
  requestID: string
  /**
   * Atomic batch: any validation or state conflict fails the whole call and
   * persists nothing. Only items still pending inspection are accepted.
   */
  inspections: ReturnedItemInspection[]
}

export interface SubmitReturnedItemInspectionResponse {
  requestID?: string
  requestStatus?: string
}

export interface GetReturnRefundReviewDetailResponse {
  requestID?: string
  orderID?: string
  bookingCode?: string
  shortOrderNumber?: string
  orderState?: string
  /** Request-level status: `ACTIVE`, `RESOLVED` or `CANCELLED`. */
  requestStatus?: string
  /** Server-rendered display copy for `requestStatus`. May arrive unpopulated. */
  requestStatusContent?: string
  timeline?: ReturnRefundReviewTimeline
  items?: ReturnRefundReviewItem[]
  actionOwners?: string[]
  /** Epoch milliseconds. */
  submittedAt?: number
  /** Server copy for the items section heading. May arrive unpopulated. */
  itemsSectionTitle?: string
  /** Pre-formatted request total. May arrive unpopulated. */
  totalValueDisplay?: string
  /**
   * Message Center room for the buyer conversation, once a clarify decision has
   * opened one. REQUEST-level — one room per request; which items are attached
   * to it is marked per item by `infoRequestUsed`.
   */
  roomID?: string
}

export const returnRefundApi = createApi({
  reducerPath: returnRefundApiReducerPath,
  baseQuery: returnRefundBaseQuery,
  serializeQueryArgs: serializeQueryArgsExcludingUiOptions,
  refetchOnMountOrArgChange: 300,
  tagTypes: [
    RETURN_REFUND_CACHE_TAGS.worklist,
    RETURN_REFUND_CACHE_TAGS.reviewDetail,
  ],
  endpoints: builder => ({
    listReturnRefundWorklist: builder.query<
      ListReturnRefundWorklistResponse,
      ListReturnRefundWorklistParams
    >({
      query: ({
        merchantID,
        submittedAtFrom,
        submittedAtTo,
        statusFilter,
        pagination,
      }) => ({
        url: returnRefundEndpoints.listReturnRefundWorklist,
        method: HttpMethod.POST,
        data: {
          merchantID,
          submittedAtFrom,
          submittedAtTo,
          statusFilter,
          pagination,
        },
        headers: createRequestHeaders({ merchantID }),
      }),
      providesTags: [RETURN_REFUND_CACHE_TAGS.worklist],
    }),
    getReturnRefundReviewDetail: builder.query<
      GetReturnRefundReviewDetailResponse,
      GetReturnRefundReviewDetailParams
    >({
      query: ({ merchantID, orderID }) => ({
        url: returnRefundEndpoints.getReturnRefundReviewDetail,
        method: HttpMethod.GET,
        params: { orderID },
        headers: createRequestHeaders({ merchantID }),
      }),
      providesTags: (result, error, { orderID }) => [
        { type: RETURN_REFUND_CACHE_TAGS.reviewDetail, id: orderID },
      ],
    }),
    submitReturnRefundReviewDecision: builder.mutation<
      SubmitReturnRefundReviewDecisionResponse,
      SubmitReturnRefundReviewDecisionParams
    >({
      query: ({ merchantID, orderID, requestID, decisions }) => ({
        url: returnRefundEndpoints.submitReturnRefundReviewDecision,
        method: HttpMethod.POST,
        data: { orderID, requestID, decisions },
        headers: createRequestHeaders({ merchantID }),
        // Blocks the UI for the round trip — a second Confirm mid-flight would
        // submit the same decision set twice.
        extraOptions: { showGlobalLoading: true },
      }),
      /*
       * The decision moves the request's status and bucket, so both readers are
       * stale: the open detail (this order) and the worklist rows/counters behind
       * the drawer.
       */
      invalidatesTags: (result, error, { orderID }) => [
        { type: RETURN_REFUND_CACHE_TAGS.reviewDetail, id: orderID },
        RETURN_REFUND_CACHE_TAGS.worklist,
      ],
    }),
    uploadReturnRefundMedia: builder.mutation<
      UploadReturnRefundMediaResponse,
      UploadReturnRefundMediaParams
    >({
      query: ({ merchantID, suppressErrorToast, ...body }) => ({
        url: returnRefundEndpoints.uploadReturnRefundMedia,
        method: HttpMethod.POST,
        data: body,
        headers: createRequestHeaders({ merchantID }),
        /*
         * The add-photo path names the file in its own toast, so the base
         * query's generic one would only stack a second, vaguer notification on
         * top. Removals opt back in — they have no message of their own.
         */
        extraOptions: { showError: !suppressErrorToast },
        /*
         * NOT globally blocking: photos upload as they are added, and the tile's
         * own uploading state is the feedback. A global loader here would freeze
         * the form for every photo.
         */
      }),
      /*
       * Each upload stages a server-side draft that the detail echoes back as the
       * item's `draftMedia` — the source of truth the photo list renders from, so
       * the cached detail is stale the moment an upload lands. The worklist shows
       * no draft state and stays untouched.
       */
      invalidatesTags: (result, error, { orderID }) => [
        { type: RETURN_REFUND_CACHE_TAGS.reviewDetail, id: orderID },
      ],
    }),
    submitReturnedItemInspection: builder.mutation<
      SubmitReturnedItemInspectionResponse,
      SubmitReturnedItemInspectionParams
    >({
      query: ({ merchantID, ...body }) => ({
        url: returnRefundEndpoints.submitReturnedItemInspection,
        method: HttpMethod.POST,
        data: body,
        headers: createRequestHeaders({ merchantID }),
        // Blocks the UI for the round trip — a second Confirm mid-flight would
        // submit the same inspection batch twice.
        extraOptions: { showGlobalLoading: true },
      }),
      /*
       * The inspection moves the request's status and bucket, so both readers are
       * stale: the open detail (this order) and the worklist rows/counters behind
       * the drawer.
       */
      invalidatesTags: (result, error, { orderID }) => [
        { type: RETURN_REFUND_CACHE_TAGS.reviewDetail, id: orderID },
        RETURN_REFUND_CACHE_TAGS.worklist,
      ],
    }),
  }),
})

export const {
  useListReturnRefundWorklistQuery,
  useLazyListReturnRefundWorklistQuery,
  useGetReturnRefundReviewDetailQuery,
  useSubmitReturnRefundReviewDecisionMutation,
  useUploadReturnRefundMediaMutation,
  useSubmitReturnedItemInspectionMutation,
} = returnRefundApi
